import React, { useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@material-ui/core/Typography';
import {
  ActionButtonHandle,
  FormPanel,
  RemoteTableField,
  ScrollableSource,
  TableFieldHandle,
  TableFieldToolbarProps,
} from '@eas/common-web';
import { useAutoCollapse } from '@components/form/misc/auto-collapse-hook';
import { FormTableCount } from '@components/form/table-field/form-table-count';
import { ChallengedAct, EsFile } from '@models';
import { useChallengedActColumns } from './challenged-act-columns';
import { ChallengedActDialog } from './challenged-act-dialog';
import { ChallengedActToolbar } from './challenged-act-toolbar';

export interface ChallengedActFieldProps {
  disabled: boolean;
  esFile?: EsFile;
  source?: ScrollableSource<ChallengedAct>;
}

export function ChallengedActField({
  disabled,
  esFile,
  source,
}: ChallengedActFieldProps) {
  const { panelRef } = useAutoCollapse(source?.items ?? []);

  const columns = useChallengedActColumns();

  const tableRef = useRef<TableFieldHandle>(null);
  const editRef = useRef<ActionButtonHandle>(null);

  const ToolbarComponent = useMemo(
    () =>
      function Toolbar(props: TableFieldToolbarProps) {
        return (
          <ChallengedActToolbar
            {...props}
            ref={editRef}
            disabled={disabled}
            esFile={esFile}
          />
        );
      },
    [disabled, esFile]
  );

  if (!source) {
    return <> Missing CHALLENGED_ACTS_SOURCE </>;
  }

  return (
    <>
      <FormPanel
        label={
          <FormattedMessage
            id="ES__CHALLENGED_ACTS__PANEL_TITLE"
            defaultMessage="Napadené akty"
          />
        }
        ref={panelRef}
        expandable={true}
        summary={<FormTableCount source={source} />}
      >
        <RemoteTableField
          ref={tableRef}
          disabled={disabled}
          columns={columns}
          source={source}
          maxRows={15}
          ToolbarComponent={ToolbarComponent}
          onRowClick={(_, index) => {
            if (!disabled) {
              tableRef.current?.setSelectedIndex(index);
              editRef.current?.executeAction();
            }
          }}
          highlightSelectedRow={true}
          showDetailBtnCond={() => true}
          FormFieldsComponent={() => (
            <div style={{ width: 700 }}>
              <Typography variant="body1" style={{ marginBottom: 8 }}>
                <FormattedMessage
                  id="ES__CHALLENGED_ACTS__DESCRIPTION"
                  defaultMessage="Napadený akt:"
                />
              </Typography>
              <ChallengedActDialog />
            </div>
          )}
        />
      </FormPanel>
    </>
  );
}
